import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { Car, Clock, Phone } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { isDriverService, isHairdressingService } from '@/shared/types/service-checkers'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { HairSalonReservation, HotelReservation } from '@monorepo/functions/src/types/reservations'
import { HairdressingServiceType } from '@monorepo/functions/src/types/services'
import { serviceTypeColors } from '@/shared/utils/serviceTypeColors'

interface HairSalonReservationCardProps {
    reservation: HairSalonReservation | HotelReservation
    onOrganizeCita: (r: HairSalonReservation | HotelReservation) => void
    onShowDetails?: (r: HairSalonReservation | HotelReservation) => void
}

const serviceTypeLabels: Record<HairdressingServiceType, string> = {
    bath_and_brush: 'Baño y cepillado',
    bath_and_trim: 'Baño y corte',
    stripping: 'Stripping',
    deshedding: 'Deslanado',
    brushing: 'Cepillado',
    spa: 'Spa',
    spa_ozone: 'Spa con ozono',
    knots: 'Nudos',
    nail_trimming: 'Extremadamente sucio',
}

const isHotelReservation = (reservation: HairSalonReservation | HotelReservation): reservation is HotelReservation => {
    return reservation.type === 'hotel'
}

export function HairSalonReservationCard({
    reservation,
    onOrganizeCita,
    onShowDetails,
}: HairSalonReservationCardProps) {
    const hasDriverService = reservation.additionalServices.some(isDriverService)

    const handleCardClick = (e: React.MouseEvent) => {
        // Si el clic fue en el botón de organizar o no hay función onShowDetails, no hacemos nada
        if ((e.target as HTMLElement).closest('button') || !onShowDetails) {
            return
        }
        onShowDetails(reservation)
    }

    return (
        <div
            className={cn(
                'flex h-full flex-col rounded-lg border p-3 shadow-sm',
                onShowDetails && 'cursor-pointer transition-shadow hover:shadow-md',
                isHotelReservation(reservation) ? 'border-emerald-200 bg-emerald-50' : 'border-pink-200 bg-pink-50',
            )}
            onClick={handleCardClick}
        >
            {/* Mascota y servicio */}
            <div className='mb-2 flex items-start justify-between'>
                <div className='flex flex-col'>
                    <div className='flex items-center gap-1'>
                        <div className='text-sm font-bold'>
                            {isHotelReservation(reservation) ? reservation.pets[0]?.name : reservation.pet.name}
                        </div>
                        <div className='text-xs text-gray-600'>
                            ({isHotelReservation(reservation) ? reservation.pets[0]?.breed : reservation.pet.breed})
                        </div>
                    </div>
                    <div className='text-xs text-gray-600'>{reservation.client.name}</div>
                    <div className='flex items-center gap-1 text-xs text-gray-600'>
                        <Phone className='h-3 w-3' />
                        {reservation.client.phone}
                    </div>
                </div>
                <div className='flex flex-col items-end gap-1'>
                    {reservation.additionalServices
                        .filter(isHairdressingService)
                        .flatMap(service => service.services)
                        .map((service, index) => (
                            <Badge
                                key={index}
                                variant='outline'
                                className={cn(
                                    'whitespace-nowrap border-[1.5px] px-1.5 py-0 text-[10px]',
                                    serviceTypeColors[service],
                                )}
                            >
                                {serviceTypeLabels[service]}
                            </Badge>
                        ))}
                    {hasDriverService && <Car className='h-4 w-4 text-gray-500' />}
                </div>
            </div>

            {/* Fecha y hora */}
            <div className='mt-auto flex flex-col gap-0.5 text-[11px] text-gray-600'>
                {isHotelReservation(reservation) ? (
                    <>
                        {reservation.checkInDate && (
                            <div className='flex items-center gap-1'>
                                <Clock className='h-3 w-3' />
                                <span>
                                    Entrada: {format(new Date(reservation.checkInDate), "d 'de' MMMM", { locale: es })}
                                </span>
                            </div>
                        )}
                        {reservation.checkOutDate && (
                            <div className='pl-4'>
                                Salida: {format(new Date(reservation.checkOutDate), "d 'de' MMMM", { locale: es })}
                                {reservation.checkOutTime && ` - ${reservation.checkOutTime}`}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {reservation.date && (
                            <div className='flex items-center gap-1'>
                                <Clock className='h-3 w-3' />
                                <span>{format(new Date(reservation.date), "EEEE d 'de' MMMM", { locale: es })}</span>
                            </div>
                        )}
                        {reservation.time && <div className='pl-4'>Hora solicitada: {reservation.time}</div>}
                    </>
                )}
            </div>

            {/* Botón de organizar */}
            <Button
                variant='default'
                className='mt-2 h-8 w-full text-sm'
                onClick={() => onOrganizeCita(reservation)}
            >
                Organizar cita
            </Button>
        </div>
    )
}
